import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { initGa4, trackGa4PageView } from '@/shared/analytics/ga4'
import {
  initYandexMetrica,
  trackYandexMetricaPageView,
} from '@/shared/analytics/yandex-metrica'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  type AnalyticsConsentValue,
} from '../analytics-consent'
import { publicRouteHref } from '@/shared/seo/seo-config'

export const CookieConsentBanner = () => {
  const { t } = useTranslation('common')
  const currentLang = useCurrentLang()
  const [consent, setConsent] = useState<AnalyticsConsentValue | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const privacyPolicyHref = publicRouteHref('privacy', currentLang)

  const initAnalytics = (trackCurrentPage: boolean) => {
    initGa4({ requireConsent: false })
    const didInitYandexMetrica = initYandexMetrica({ requireConsent: false })

    if (!trackCurrentPage) {
      return
    }

    trackGa4PageView(window.location.href, document.title, {
      requireConsent: false,
    })

    if (!didInitYandexMetrica) {
      trackYandexMetricaPageView(window.location.href, {
        requireConsent: false,
      })
    }
  }

  useEffect(() => {
    const savedConsent = getAnalyticsConsent()

    setConsent(savedConsent)

    if (savedConsent === 'accepted') {
      initAnalytics(false)
    }

    setIsInitialized(true)
  }, [])

  const handleAccept = () => {
    setAnalyticsConsent('accepted')
    setConsent('accepted')
    initAnalytics(true)
  }

  const handleReject = () => {
    setAnalyticsConsent('rejected')
    setConsent('rejected')
  }

  if (!isInitialized || consent) {
    return null
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50">
      <Card
        className="pointer-events-auto w-full rounded-none border-x-0 border-b-0 border-border/80 bg-background/80 py-0 shadow-xl backdrop-blur-md"
        role="dialog"
        aria-label={t('cookieConsentTitle')}
      >
        <CardContent className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-4 text-left sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
          <div className="min-w-0 max-w-4xl space-y-1.5">
            <h2 className="text-base font-medium text-foreground">
              {t('cookieConsentTitle')}
            </h2>
            <p className="text-base leading-7 text-muted-foreground">
              {t('cookieConsentDescriptionBeforeReject')}
              <button
                className="text-muted-foreground/85 underline underline-offset-4 transition-colors hover:text-muted-foreground"
                type="button"
                onClick={handleReject}
              >
                {t('cookieConsentRejectLink')}
              </button>
              {t('cookieConsentDescriptionBeforePrivacy')}
              <a
                className="font-medium text-foreground underline underline-offset-4 transition-colors hover:text-primary"
                href={privacyPolicyHref}
              >
                {t('cookieConsentPrivacyPolicy')}
              </a>
              {t('cookieConsentDescriptionAfterPrivacy')}
            </p>
          </div>

          <div className="flex w-full shrink-0 flex-col-reverse justify-end gap-2 sm:w-auto sm:flex-row">
            <Button
              className="h-10 w-full whitespace-nowrap px-5 text-base sm:w-auto"
              type="button"
              onClick={handleAccept}
            >
              {t('cookieConsentAccept')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
